/**
 * PauseResultCard — 이미 제출된 point 재열람.
 * Split 레이아웃 사이드 상단.
 * Spec §7.3
 *
 * NOTE: `PauseQuizResponseSummary` 는 최소 필드만 담고 있어 (isCorrect · isOfflineCompleted)
 * correctAnswers/description 은 미포함. 재열람 UI 는 bubble + 상태 텍스트만 노출.
 */
import { BookOpen, Check, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { PauseQuizResponseSummary } from '@/api/lcLearning'
import { Button } from '../ui/Button'

interface Props {
  pointIndex: number
  response: PauseQuizResponseSummary
  onResume: () => void
}

type ResultKind = 'correct' | 'wrong' | 'offline'

function resolveKind(response: PauseQuizResponseSummary): ResultKind {
  if (response.isOfflineCompleted) return 'offline'
  if (response.isCorrect) return 'correct'
  return 'wrong'
}

export default function PauseResultCard({
  pointIndex,
  response,
  onResume,
}: Props) {
  const { t } = useTranslation('learner')
  const kind = resolveKind(response)

  const bubbleClass =
    kind === 'correct'
      ? 'bg-lc-success-500'
      : kind === 'wrong'
        ? 'bg-lc-error-500'
        : 'bg-lc-key-500'

  const titleClass =
    kind === 'correct'
      ? 'text-lc-text-success'
      : kind === 'wrong'
        ? 'text-lc-text-error'
        : 'text-lc-text-link'

  const title =
    kind === 'correct'
      ? t('pause.resultCorrect')
      : kind === 'wrong'
        ? t('pause.resultWrong')
        : t('pause.resultOffline')

  const Icon =
    kind === 'correct' ? Check : kind === 'wrong' ? X : BookOpen

  return (
    <div className="flex h-full flex-col gap-6 rounded-3xl bg-lc-bg-primary p-6 shadow-[0_4px_4px_rgba(0,0,0,0.05)]">
      <div className="flex items-center gap-3">
        <div
          className={[
            'size-10 rounded-full flex items-center justify-center text-white shrink-0',
            bubbleClass,
          ].join(' ')}
          aria-label={kind}
        >
          <Icon className="size-5" strokeWidth={3} aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className={[
              'text-lc-v3-h4 font-bold',
              titleClass,
            ].join(' ')}
          >
            {title}
          </h3>
          <p className="text-lc-v3-label-md font-bold text-lc-text-tertiary">
            {t('pause.problemLabel', { n: pointIndex + 1 })}
          </p>
        </div>
      </div>

      <div className="mt-auto flex justify-center pt-1">
        <Button variant="navy" size="L" className="w-full" onClick={onResume}>
          {t('pause.resumeVideo')}
        </Button>
      </div>
    </div>
  )
}
